import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  effect
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { DialogTypeEnum } from "src/app/classes/enums";
import { CarBrandPipe } from "src/app/pipes/car-brand.pipe";
import { FormService } from "src/app/services/form.service";
import { PagingService } from "src/app/services/paging.service";
import { TrackingService } from "src/app/services/tracking.service";
import {
  CampaignResponse,
  DiscountTypeEnum,
  VehicleSearchResponse
} from "src/domain/client";
import { DialogComponent } from "../../layout/dialog/dialog.component";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { TextBoxComponent } from "../../inputs/text-box/text-box.component";
import { ButtonComponent } from "../../inputs/button/button.component";
import { TextAreaComponent } from "../../inputs/text-area/text-area.component";
import { SecondaryConfirmationBoxComponent } from "../../layout/secondary-confirmation-box/secondary-confirmation-box.component";
import { CheckboxComponent } from "../../inputs/checkbox/checkbox.component";
import { FirstToUpperPipe } from "../../../pipes/sharedpipes/first-to-upper.pipe";
import { Store } from "@ngrx/store";
import { DiscountCodeValidationStateEnum } from "src/app/state/utils/booking.modals";
import {
  clearCampaignCode,
  contactInfoPageLoaded,
  selectCampaignResponse,
  selectCampaignValidationState,
  selectCarInfo,
  selectProposalCustomer,
  selectSelectedPackages,
  updateContactInformation,
  validateCampaignCode
} from "src/app/state";
import { toSignal } from "@angular/core/rxjs-interop";
import { debounceTime } from "rxjs";

@Component({
  selector: "app-contact-information",
  templateUrl: "./contact-information.component.html",
  imports: [
    PageContainerComponent,
    TextBoxComponent,
    ButtonComponent,
    TextAreaComponent,
    SecondaryConfirmationBoxComponent,
    CheckboxComponent,
    FirstToUpperPipe,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactInformationComponent
  extends PageBaseComponent
  implements OnInit
{
  readonly validationState: Signal<DiscountCodeValidationStateEnum>;
  readonly campaignResponse: Signal<CampaignResponse | null>;
  readonly carInfo: Signal<VehicleSearchResponse | null>;

  readonly DiscountTypeEnum = DiscountTypeEnum;
  discountCodeValidationState = DiscountCodeValidationStateEnum.NotValidated;
  DiscountCodeValidationStateEnum = DiscountCodeValidationStateEnum;

  public DialogType: typeof DialogTypeEnum = DialogTypeEnum;
  constructor(
    public formService: FormService,
    private trackingService: TrackingService,
    public pagingService: PagingService,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private carBrandPipe: CarBrandPipe,
    private store: Store
  ) {
    super();
    if (
      !!this.formService.contactInformationForm.pristine &&
      !this.formService.contactInformationForm.valid
    )
      this.trackingService.trackContactFormStarted();
    this.formService.contactInformationForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.contactInformationForm.value;

    this.validationState = this.store.selectSignal(
      selectCampaignValidationState
    );
    this.campaignResponse = this.store.selectSignal(selectCampaignResponse);
    this.carInfo = this.store.selectSignal(selectCarInfo);
    const selectedPackages = this.store.selectSignal(selectSelectedPackages);

    effect(() => {
      const otherValue = selectedPackages()?.other;
      const contactForm = this.formService.contactInformationForm;
      if (otherValue && !contactForm.controls.other.value) {
        contactForm.controls.other.patchValue(otherValue, { emitEvent: false });
      }
    });

    const formChanges = toSignal(
      this.formService.contactInformationForm.valueChanges.pipe(
        debounceTime(300)
      )
    );

    effect(() => {
      const changes = formChanges();
      if (changes) {
        this.store.dispatch(updateContactInformation({ info: changes }));
      }
    });

    effect(() => {
      const proposalCustomer = this.store.selectSignal(
        selectProposalCustomer
      )();
      if (proposalCustomer) {
        this.formService.contactInformationForm.patchValue(
          {
            firstName: proposalCustomer.firstName,
            lastName: proposalCustomer.lastName,
            phone: proposalCustomer.phone,
            email: proposalCustomer.email,
            smsChecked: proposalCustomer.sendSms
          },
          { emitEvent: false }
        );
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(contactInfoPageLoaded());
  }

  validateDiscountCode(): void {
    const code =
      this.formService.contactInformationForm.get("discountCode")?.value;
    if (code) {
      this.store.dispatch(validateCampaignCode({ code }));
    }
  }

  clearDiscountCode() {
    this.formService.contactInformationForm.controls.discountCode.enable();
    this.store.dispatch(clearCampaignCode());
  }

  openModal(type: DialogTypeEnum, event?: MouseEvent): void {
    event?.preventDefault();
    const title = this.translateService.instant("dialog." + type);
    document.getElementById("vbo-header").scrollIntoView(true);
    const ref = this.dialog.open(DialogComponent, {
      data: {
        componentName: type,
        title,
        brand: this.carBrandPipe.transform(this.carInfo())
      },
      maxWidth: "600px",
      width: "90%"
    });

    const _obs = ref.afterClosed();
  }
}
